import { Injectable } from '@angular/core';
import { lazyInject } from '@app/containers/base/decorators/lazy-inject.decorator';
import {
  Observable,
  observableCombineLatest,
  observableOf,
  rx,
} from '@app/shared/rx/rx-wrapper';
import { IApiListResponse } from '@app/shared/types/common/epics.type';
import { IDocumentOperationOptions } from '@app/shared/types/entities/base/base.type';
import { IHawkerSession } from '@app/shared/types/entities/hawker-session/hawker-session.types';
import { find, forEach, get, isEmpty, map } from 'lodash';
import { BaseInjectService } from '../base/base-inject.service';
import { XprtDataService } from '../xprt-data/xprt-data.service';

@Injectable()
export class HawkerSessionExtraDataService extends BaseInjectService {
  @lazyInject(XprtDataService) public xprtDataService: XprtDataService;

  public fetchSiteAndEventInfoForSessions(
    hawkerSessionsResponse: IApiListResponse,
    options: IDocumentOperationOptions & {
      withSiteInfo?: boolean;
      withEventInfo?: boolean;
    },
  ): Observable<IApiListResponse> {
    const { withSiteInfo = false, withEventInfo = false } = options ?? {};

    if (
      (!withSiteInfo && !withEventInfo) ||
      isEmpty(get(hawkerSessionsResponse, 'items', []))
    ) {
      return observableOf<IApiListResponse>(hawkerSessionsResponse);
    }

    const hawkerSessions = get(
      hawkerSessionsResponse,
      'items',
      [],
    ) as IHawkerSession[];

    const siteSet = new Set<string>();
    const eventSet = new Set<string>();

    forEach(hawkerSessions, (session) => {
      if (withSiteInfo) {
        const siteId = get(session, 'site_id');
        if (!isEmpty(siteId)) {
          siteSet.add(siteId);
        }
      }

      if (withEventInfo) {
        const eventId = get(session, 'event.id');
        if (!isEmpty(eventId)) {
          eventSet.add(eventId);
        }
      }
    });

    return observableCombineLatest([
      isEmpty(siteSet)
        ? observableOf([])
        : this.xprtDataService.getSitesByIds(Array.from(siteSet), {
            select: ['name', 'store_number', 'id'],
          }),
      isEmpty(eventSet)
        ? observableOf([])
        : this.xprtDataService.getEventsByIds(Array.from(eventSet), {
            select: ['name', 'id'],
          }),
    ]).pipe(
      rx.take(1),
      rx.map(([sites, events]) => {
        const hawkerSessionsWithDetails = map(hawkerSessions, (session) => {
          const site = find(sites, { id: get(session, 'site_id') });

          return {
            ...session,
            site_name: get(site, 'name', ''),
            site_store_number: get(site, 'store_number', ''),
            event: {
              ...get(session, 'event', {}),
              ...find(events, { id: get(session, ['event', 'id']) }),
            },
          };
        });

        return {
          ...hawkerSessionsResponse,
          items: hawkerSessionsWithDetails,
        } as IApiListResponse;
      }),
    );
  }
}
